
import { defineComponent, PropType } from 'vue'

import AnalyzerListMixin from '~/mixins/analyzerListMixin'
import { AnalyzerInterface } from '~/store/analyzer/list'

import { AnalyzerAnalyzerType } from '~/types/types'

export default defineComponent({
  name: 'AnalyzerSearch',
  emits: ['addAnalyzer', 'removeAnalyzer', 'closeSearch'],
  mixins: [AnalyzerListMixin],
  props: {
    analyzers: {
      type: Array as PropType<AnalyzerInterface[] | null>,
      default: null
    },
    selectedAnalyzers: {
      type: Array as PropType<string[]>,
      default: () => []
    },
    toggleSearch: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    closeOnAdd: {
      type: Boolean,
      default: false
    },
    dropdownBgClass: {
      type: String,
      default: 'bg-ink-300'
    },
    isProcessing: {
      type: Boolean,
      default: false
    },
    showSelectedAnalyzers: {
      type: Boolean,
      default: true
    },
    analyzerTypes: {
      type: Array as PropType<AnalyzerAnalyzerType[]>,
      default: () => [
        AnalyzerAnalyzerType.Community,
        AnalyzerAnalyzerType.Core,
        AnalyzerAnalyzerType.Custom
      ]
    },
    mode: {
      type: String,
      default: 'analyzer',
      validator: (val: string) => ['analyzer', 'formatter'].includes(val)
    },
    inputDisabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchCandidate: '',
      showAnalyzerList: false,
      AnalyzerAnalyzerType,
      hoveredAnalyzer: null as string | null
    }
  },
  async fetch(): Promise<void> {
    if (this.analyzers === null) {
      await this.fetchAnalyzers()
    }
  },
  computed: {
    effectiveAnalyzerList(): AnalyzerInterface[] {
      return this.analyzers === null ? this.analyzerList : this.analyzers
    },
    searchAnalyzers(): Record<AnalyzerAnalyzerType, AnalyzerInterface[]> {
      const analyzersNotYetSelected: Record<AnalyzerAnalyzerType, AnalyzerInterface[]> = {
        [AnalyzerAnalyzerType.Core]: [],
        [AnalyzerAnalyzerType.Community]: [],
        [AnalyzerAnalyzerType.Custom]: []
      }

      for (const analyzer of this.effectiveAnalyzerList) {
        /**
         * Checks if the analyzer should be displayed based on the following conditions:
         * - If `showSelectedAnalyzers` is false or `selectedAnalyzers` does not include the analyzer's name.
         * - If `searchCandidate` is empty or the analyzer's name (in lowercase) includes the `searchCandidate` (in lowercase).
         */
        if (
          !(this.showSelectedAnalyzers && this.selectedAnalyzers.includes(analyzer.name)) &&
          (!this.searchCandidate ||
            analyzer.name.toLowerCase().includes(this.searchCandidate.toLowerCase()))
        ) {
          analyzersNotYetSelected[analyzer.analyzerType].push(analyzer)
        }
      }

      return analyzersNotYetSelected
    },
    searchTransformers(): any[] {
      if (this.mode !== 'formatter') return []

      const allTransformers: any[] = []
      this.effectiveAnalyzerList.forEach((analyzer) => {
        if (analyzer.transformers) {
          analyzer.transformers.forEach((transformer) => {
            // Check if already selected or filtered out
            // Use transformer.shortcode as the unique identifier (mapped to 'name' below)
            if (
              !(
                this.showSelectedAnalyzers && this.selectedAnalyzers.includes(transformer.shortcode)
              ) &&
              (!this.searchCandidate ||
                transformer.name.toLowerCase().includes(this.searchCandidate.toLowerCase()))
            ) {
              allTransformers.push({
                ...transformer,
                name: transformer.shortcode, // Map shortcode to name (ID)
                label: transformer.name, // Map name to label (Display)
                analyzerType: 'TRANSFORMER' // Fake type
              })
            }
          })
        }
      })
      return allTransformers
    }
  },
  methods: {
    toggleAnalyzer(analyzer: AnalyzerInterface) {
      if (this.selectedAnalyzers.includes(analyzer.name)) {
        this.$emit('removeAnalyzer', analyzer)
      } else {
        this.$emit('addAnalyzer', analyzer)
      }

      if (this.closeOnAdd) {
        this.showAnalyzerList = false
        this.searchCandidate = ''
      } else {
        this.showAnalyzerList = true
        // Keep searchCandidate to preserve the list state
      }
    },
    toggleAnalyzerList(): void {
      this.showAnalyzerList = !this.showAnalyzerList
    },
    outsideClickHandler(event: InputEvent): void {
      const addButton = this.$parent?.$refs['add-analyzer-button'] as Vue
      if (addButton) {
        const el = addButton.$el
        // skipcq: JS-0050 - We don't want to do an exact match here as I am unsure if $el by Vue will be a proper reference to the element
        if (event.target == el || el.contains(event.target as Element)) {
          return
        }
      }
      this.showAnalyzerList = false
      this.$emit('closeSearch')
    }
  }
})
